import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import ProUpsell from "@/components/ProUpsell";

/**
 * Voice-to-Journal recorder. The user taps once to record, again to stop.
 * On stop, we upload the WebM/Opus blob to /journal-voice/transcribe and
 * pass the structured fields back to the parent via `onResult`.
 *
 * Props:
 *   onResult({ transcript, observations, actions_taken, mood, weather, temperature })
 */
export default function VoiceJournalRecorder({ onResult }) {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const [elapsed, setElapsed] = useState(0);
  const [upsell, setUpsell] = useState(false);
  const [lastTranscript, setLastTranscript] = useState("");
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    if (state !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // MediaRecorder picks the best supported codec automatically.
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      mr.onstop = handleStop;
      mr.start();
      setState("recording");
      startedAtRef.current = Date.now();
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
    } catch (e) {
      toast.error("Microphone access denied — check your browser permissions.");
    }
  };

  const stop = () => {
    if (state !== "recording") return;
    setState("processing");
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try {
      mediaRecorderRef.current?.stop();
    } catch { /* already stopped */ }
  };

  const handleStop = async () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
    if (blob.size < 1024) {
      toast.message("Recording too short");
      setState("idle");
      return;
    }
    if (blob.size > 25 * 1024 * 1024) {
      toast.error("Recording too long (max 25 MB)");
      setState("idle");
      return;
    }

    try {
      const fd = new FormData();
      fd.append("audio", blob, "memo.webm");
      const r = await api.post("/journal-voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60_000,
      });
      const { transcript, observations, actions_taken, mood, weather, temperature } = r.data;
      setLastTranscript(transcript);
      onResult?.({ observations, actions_taken, mood, weather, temperature, transcript });
      toast.success("Transcribed and filled in. Review before saving.");
    } catch (e) {
      if (e.response?.status === 402) setUpsell(true);
      else toast.error(e.response?.data?.detail || "Transcription failed");
    } finally {
      setState("idle");
    }
  };

  const formattedTime = () => {
    const m = Math.floor(elapsed / 60).toString().padStart(2, "0");
    const s = (elapsed % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div className="border border-amber-200 bg-amber-50/40 rounded-xl p-3" data-testid="voice-journal-recorder">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">Pro</span>
          <Sparkles className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-medium text-stone-800">Voice to journal</span>
        </div>
        <div className="flex items-center gap-2">
          {state === "recording" && (
            <span className="text-xs font-mono tabular-nums text-rose-700 inline-flex items-center gap-1.5" data-testid="voice-recording-timer">
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
              {formattedTime()}
            </span>
          )}
          {state === "idle" && (
            <Button
              size="sm"
              type="button"
              onClick={start}
              className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold"
              data-testid="voice-start-btn"
            >
              <Mic className="w-4 h-4 mr-1.5" /> Record
            </Button>
          )}
          {state === "recording" && (
            <Button
              size="sm"
              type="button"
              onClick={stop}
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="voice-stop-btn"
            >
              <Square className="w-4 h-4 mr-1.5 fill-current" /> Stop
            </Button>
          )}
          {state === "processing" && (
            <span className="text-xs text-stone-600 inline-flex items-center gap-1.5" data-testid="voice-processing">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Transcribing…
            </span>
          )}
        </div>
      </div>
      {state === "idle" && !lastTranscript && (
        <p className="text-xs text-stone-500 mt-1.5">
          Tap Record and describe what you see. We'll transcribe and fill in the form below.
        </p>
      )}
      {lastTranscript && state === "idle" && (
        <div className="mt-2 text-xs text-stone-600">
          <div className="flex items-start justify-between gap-2">
            <div className="font-medium text-stone-700">Last transcript</div>
            <button
              type="button"
              onClick={() => setLastTranscript("")}
              className="text-stone-400 hover:text-stone-600"
              data-testid="voice-clear-transcript"
              aria-label="Clear transcript"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="italic text-stone-600 mt-1 max-h-24 overflow-y-auto">"{lastTranscript}"</p>
        </div>
      )}
      <ProUpsell open={upsell} onOpenChange={setUpsell} feature="voice_journal" />
    </div>
  );
}
